import { PackageProviderIcon } from 'components/shared/ppoints/PackageProviderIcon';
import { STooltipWrapper } from 'components/shared/s-polaris/components/polaris/s-tooltip-wrapper';
import { PackageProviderName } from 'data/ppoints/constants';
import { AggregatorPackageProvider, isAggregatorPackageProvider } from 'models/ppoints/CountryWithAggregatedProviders';
import { PackageProvider } from 'models/ppoints/PackageProvider';
import { useTranslation } from 'react-i18next';
import PackageProviderService from 'services/settings/ppoints/PackageProviderService';
import { classNames } from 'utils/class-names';
import { PackageProviderFeature } from '../../../../../pb/responses/v1/package_provider_features_response_pb';
import { useSelectAggregatorProvidersModal } from './hooks/useSelectAggregatorProvidersModal';
import { ShippingProviderCardSimplifiedIconWithHeader } from './ShippingProviderCardSimplifiedIconWithHeader';
import { ShippingProviderCardSimplifiedShell } from './ShippingProviderCardSimplifiedShell';

export const ShippingProviderCardSimplified: React.FC<ShippingProviderCardSimplifiedProps> = (props) => {
    const { t } = useTranslation();
    const { openSelectAggregatorProvidersModal } = useSelectAggregatorProvidersModal();

    const { provider } = props;
    const comingSoon = props.features.includes(PackageProviderFeature.COMING_SOON);

    // Only the aggregator's own carriers the merchant already picked in the modal, e.g. Kvikk's gls/dpd.
    const selectedAggregatorProviders = isAggregatorPackageProvider(provider)
        ? provider.providers.filter((p) => props.selectedProviders.includes(p.name))
        : [];

    const selected = isAggregatorPackageProvider(provider)
        ? selectedAggregatorProviders.length > 0
        : props.selectedProviders.includes(provider.name);

    const handleSelect = () => {
        if (isAggregatorPackageProvider(provider)) {
            openSelectAggregatorProvidersModal(
                provider,
                selectedAggregatorProviders.map((p) => p.name),
                (providerNames) => props.onSelectAggregatorProviders(provider, providerNames)
            );
            return;
        }

        props.onSelect(provider.name);
    };

    return (
        <ShippingProviderCardSimplifiedShell
            selected={selected}
            disabled={props.disabled}
            onSelect={comingSoon ? undefined : handleSelect}
        >
            <ShippingProviderCardSimplifiedIconWithHeader providerName={provider.name} />

            {selectedAggregatorProviders.length > 0 && (
                <STooltipWrapper
                    uniqueId={`${provider.id}`}
                    tooltipContent={selectedAggregatorProviders
                        .map((selectedProvider) => PackageProviderService.getDisplayName(selectedProvider.name))
                        .join(', ')}
                >
                    <div className="flex items-center shrink-0">
                        {selectedAggregatorProviders.map((selectedProvider, index) => (
                            <div
                                key={selectedProvider.id}
                                className={classNames(index > 0 && '-ml-1.5')}
                                style={{ zIndex: selectedAggregatorProviders.length + index }}
                            >
                                <PackageProviderIcon provider={selectedProvider.name} size="small-200" />
                            </div>
                        ))}
                    </div>
                </STooltipWrapper>
            )}

            {comingSoon && (
                <div className="shrink-0">
                    <s-badge>{t('GENERAL.COMING_SOON')}</s-badge>
                </div>
            )}
        </ShippingProviderCardSimplifiedShell>
    );
};

interface ShippingProviderCardSimplifiedProps {
    provider: PackageProvider | AggregatorPackageProvider;
    features: PackageProviderFeature[];
    selectedProviders: PackageProviderName[];
    disabled?: boolean;
    onSelect: (providerName: PackageProviderName) => void;
    onSelectAggregatorProviders: (aggregator: AggregatorPackageProvider, providerNames: PackageProviderName[]) => void;
}
